import axios from 'axios';
import * as Sentry from '@sentry/react';

// Em produção o nginx do próprio frontend faz proxy de /api para o backend,
// então a origem é a mesma e não há CORS. VITE_API_URL permite apontar para
// um backend externo quando necessário (é o que o build do CapRover faz).
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
});

// Interceptor para adicionar o token em todas as requisições
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normaliza o caminho para agrupar erros no Sentry: /documents/1 e
// /documents/2 são a MESMA falha — sem isto viram grupos distintos (além de
// estourar o limite de cardinalidade das tags).
function endpointPattern(url = '') {
  return url
    .split('?')[0]
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d+/g, '/:id');
}

// Reporta falha de resposta (5xx) ou de rede (API fora do ar, DNS, CORS,
// timeout) ao Sentry. 4xx é uso esperado (CPF/email inválido, token expirado,
// permissão) e não deve virar evento.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const method = (error.config?.method || 'get').toUpperCase();
    const pattern = endpointPattern(error.config?.url);
    const status = error.response?.status;

    if (status === undefined) {
      // error.response ausente = a requisição nem chegou a ter resposta
      // (rede caiu, CORS, timeout).
      Sentry.withScope((scope) => {
        scope.setTag('api_endpoint', pattern);
        scope.setTag('api_status', 'network_error');
        scope.setContext('request', { method, url: error.config?.url });
        Sentry.captureException(error);
      });
    } else if (status >= 500) {
      Sentry.withScope((scope) => {
        scope.setTag('api_endpoint', pattern);
        scope.setTag('api_status', String(status));
        scope.setContext('request', { method, url: error.config?.url, status });
        Sentry.captureException(new Error(`API ${status} ${method} ${pattern}`));
      });
    }

    return Promise.reject(error);
  },
);

export const authService = {
  login: async (cpf, email) => {
    const response = await api.post('/auth/login', { cpf, email });
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const documentsService = {
  listDocuments: async () => {
    const response = await api.get('/documents');
    return response.data;
  },
  
  downloadDocument: async (id) => {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export const adminService = {
  login: async (username, password) => {
    const response = await api.post('/admin/login', { username, password });
    return response.data;
  },

  getAuthHeader: () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer admin:${token}` };
  },

  listFiles: async () => {
    const response = await api.get('/admin/files', {
      headers: adminService.getAuthHeader(),
    });
    return response.data;
  },

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/files/upload', formData, {
      headers: {
        ...adminService.getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteFile: async (filename) => {
    const response = await api.delete(`/admin/files/${encodeURIComponent(filename)}`, {
      headers: adminService.getAuthHeader(),
    });
    return response.data;
  },

  listConselheiros: async () => {
    const response = await api.get('/admin/conselheiros', {
      headers: adminService.getAuthHeader(),
    });
    return response.data;
  },

  uploadConselheiros: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/conselheiros/upload', formData, {
      headers: {
        ...adminService.getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadConselheiros: async () => {
    const response = await api.get('/admin/conselheiros/download', {
      headers: adminService.getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  listAdmins: async () => {
    const response = await api.get('/admin/admins', {
      headers: adminService.getAuthHeader(),
    });
    return response.data;
  },

  createAdmin: async (username, password) => {
    const response = await api.post(
      '/admin/admins',
      { username, password },
      { headers: adminService.getAuthHeader() },
    );
    return response.data;
  },

  deleteAdmin: async (username) => {
    const response = await api.delete(
      `/admin/admins/${encodeURIComponent(username)}`,
      { headers: adminService.getAuthHeader() },
    );
    return response.data;
  },
};

export default api;
